<template>
  <v-form ref="form" v-model="valid" validate-on="submit lazy">
    <h2 class="text-subtitle-2 font-weight-medium my-3">Chit Details</h2>
    <v-row v-if="chitData.chit_detail">
      <v-col cols="12" md="6">
        <ChitTypeDropdown
          v-model="chitData.chit_types"
          label="Chit Type*"
          @update:model-value="clearErrors()"
          :rules="[requiredValidator]"
          :error-messages="error && (error.fieldErrors['chit_types'] as string[])"
          multiple
          :disabled="isEdit"
          return-object
        ></ChitTypeDropdown>
      </v-col>
      <!-- Only managers can approve chits so i use this permission implicitly as an assign guards Permission -->
      <v-col cols="12" md="6" v-if="currentUser?.hasPermission('chits.assign_guards_chit')">
        <EmployeeAutoCompletePicker
          v-model="chitData.guards"
          label="Guards*"
          variant="outlined"
          hide-details="auto"
          density="compact"
          :rules="[requiredValidator]"
          @update:model-value="clearErrors()"
          multiple
          :error-messages="error && (error.fieldErrors['guards'] as string[])"
          :queryFilters="{ is_active: 'true' }"
        ></EmployeeAutoCompletePicker>
      </v-col>
      <v-col cols="12" md="6">
        <DateTimePickerField
          v-model="chitData.chit_detail.arrived_at"
          label="Arrived At"
          variant="outlined"
          hide-details="auto"
          density="compact"
          clearable
          @click:clear="chitData.chit_detail.arrived_at = null"
          @update:model-value="clearErrors()"
          :error-messages="error && (error.fieldErrors['arrived_at'] as string[])"
        ></DateTimePickerField>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="chitData.chit_detail.ref_number"
          label="Event/Ref #"
          variant="outlined"
          hide-details="auto"
          density="compact"
          @update:model-value="clearErrors()"
          :error-messages="error && (error.fieldErrors['ref_number'] as string[])"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="chitData.chit_detail.call_name"
          label="Caller/Name"
          variant="outlined"
          hide-details="auto"
          density="compact"
          @update:model-value="clearErrors()"
          :error-messages="error && (error.fieldErrors['call_name'] as string[])"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="chitData.chit_detail.call_type"
          label="Alarm/Call type"
          variant="outlined"
          hide-details="auto"
          density="compact"
          @update:model-value="clearErrors()"
          :error-messages="error && (error.fieldErrors['call_type'] as string[])"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <ClientAutoCompletePicker
          v-model="chitData.client"
          label="Client*"
          variant="outlined"
          hide-details="auto"
          density="compact"
          :rules="[requiredValidator]"
          @update:model-value="updateChitSiteAndAddress()"
          :error-messages="error && (error.fieldErrors['client'] as string[])"
          clearable
          :allowCreateClient="true"
        />
      </v-col>
      <v-col cols="12" md="6">
        <SiteAutoCompletePicker
          :disabled="!selectedClient"
          :client="selectedClient"
          v-model="selectedSite"
          :label="`Site${!showAddressFields ? '*' : ''}`"
          variant="outlined"
          hide-details="auto"
          density="compact"
          @update:model-value="updateChitAddress"
          @create-site-pressed="enableSiteCreationForm"
          :rules="[!showAddressFields ? requiredValidator : true]"
          :allowSiteCreation="true"
          :error-messages="error && (error.fieldErrors['site'] as string[])"
          clearable
          return-object
        />
      </v-col>
      <v-col cols="12" v-if="showAddressFields">
        <h2 class="text-subtitle-2 font-weight-medium my-3">Site Info</h2>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="chitNewSite.name"
              label="Site Name"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :error-messages="error && (error.fieldErrors['name'] as string[])"
            ></v-text-field>
          </v-col>
        </v-row>
        <h2 class="text-subtitle-2 font-weight-medium my-3">Add site address</h2>

        <v-row v-if="chitNewSite.address">
          <v-col cols="12">
            <v-text-field
              v-model="chitNewSite.address.address1"
              label="Address*"
              variant="outlined"
              hide-details="auto"
              density="compact"
              :rules="[requiredValidator]"
              @update:model-value="clearErrors()"
              :error-messages="
                error &&
                error.fieldErrors['site'] &&
                ((error.fieldErrors['site'] as IFieldErrors)['address'] as IFieldErrors) &&
                (((error.fieldErrors['site'] as IFieldErrors)['address'] as IFieldErrors)[
                  'address1'
                ] as string[])
              "
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="chitNewSite.address.city"
              label="City*"
              variant="outlined"
              hide-details="auto"
              density="compact"
              :rules="[requiredValidator]"
              @update:model-value="clearErrors()"
              :error-messages="
                error &&
                error.fieldErrors['site'] &&
                ((error.fieldErrors['site'] as IFieldErrors)['address'] as IFieldErrors) &&
                (((error.fieldErrors['site'] as IFieldErrors)['address'] as IFieldErrors)[
                  'city'
                ] as string[])
              "
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <RegionComboBox
              v-model="chitNewSite.address.region"
              label="Region*"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :rules="[requiredValidator]"
              :error-messages="
                error &&
                error.fieldErrors['site'] &&
                ((error.fieldErrors['site'] as IFieldErrors)['address'] as IFieldErrors) &&
                (((error.fieldErrors['site'] as IFieldErrors)['address'] as IFieldErrors)[
                  'region'
                ] as string[])
              "
              :country="addressCountryCode"
            ></RegionComboBox>
          </v-col>
          <v-col cols="12" md="6">
            <CountryPicker
              v-model="chitNewSite.address.country"
              label="Country*"
              variant="outlined"
              hide-details="auto"
              density="compact"
              :rules="[requiredValidator]"
              :error-messages="
                error &&
                error.fieldErrors['site'] &&
                ((error.fieldErrors['site'] as IFieldErrors)['address'] as IFieldErrors) &&
                (((error.fieldErrors['site'] as IFieldErrors)['address'] as IFieldErrors)[
                  'country'
                ] as string[])
              "
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="chitNewSite.address.postal_code"
              label="Postal Code"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :error-messages="
                error &&
                error.fieldErrors['site'] &&
                ((error.fieldErrors['site'] as IFieldErrors)['address'] as IFieldErrors) &&
                (((error.fieldErrors['site'] as IFieldErrors)['address'] as IFieldErrors)[
                  'postal_code'
                ] as string[])
              "
            ></v-text-field>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-form>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useAuthStore } from '@/stores'
import type { IFieldErrors, ISystemError } from '@/models/error'
import type { VForm } from 'vuetify/components'
import { type IChitData } from '@/models/chit'

import { requiredValidator } from '@/utils/validators'
import { Site, type ISite, type ISiteData } from '@/models/client'
import type { IAddressData } from '@/models/address'
import { useCreateSite } from '@/composables/site'

import ClientAutoCompletePicker from '../clients/ClientAutoCompletePicker.vue'
import ChitTypeDropdown from '../chits/ChitTypeDropdown.vue'
import DateTimePickerField from '../common/DateTimePickerField.vue'
import SiteAutoCompletePicker from '../sites/SiteAutoCompletePicker.vue'
import EmployeeAutoCompletePicker from '../employees/EmployeeAutoCompletePicker.vue'
import CountryPicker from '../common/CountryPicker.vue'
import RegionComboBox from '../common/RegionComboBox.vue'

interface Props {
  error?: ISystemError | null
  isEdit?: boolean
}

defineProps<Props>()

const chitData = defineModel<IChitData>({ required: true })

const chitNewSite = ref<ISiteData>({
  name: '',
  address: {} as IAddressData
} as ISiteData)

const addressCountryCode = computed(() => {
  return typeof chitNewSite.value?.address?.country === 'string'
    ? chitNewSite.value.address.country
    : chitNewSite.value?.address?.country?.code
})

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

const form = ref<VForm>()
const valid = ref(false)

const showAddressFields = ref(false)
const selectedSite = ref<number | ISiteData | null>(chitData.value.site || null)

watch(selectedSite, (newValue) => {
  if (newValue) {
    chitData.value.site = newValue
  }
})

async function validateForm() {
  clearErrors()
  const { valid } = await form.value!.validate()
  return valid
}

function resetFormValidation() {
  form.value?.resetValidation()
}

function clearErrors() {
  resetFormValidation()
}

const selectedClient = computed(() => {
  return typeof chitData.value.client == 'number'
    ? chitData.value.client
    : chitData.value.client?.id
})

function updateChitSiteAndAddress() {
  clearErrors()
  chitNewSite.value = { address: {} } as ISiteData
  selectedSite.value = null
  showAddressFields.value = false
}

function updateChitAddress(site: number | ISiteData | null) {
  clearErrors()
  if (site) {
    showAddressFields.value = false
  }
}

function enableSiteCreationForm() {
  showAddressFields.value = true
  selectedSite.value = null
}

const { mutate: createSite, reset: createSiteReset } = useCreateSite()

async function createClientSite() {
  clearErrors()
  createSiteReset()
  const payload = {
    clientId: selectedClient.value,
    site: {
      ...chitNewSite.value
    }
  }

  return new Promise<ISite>((resolve, reject) => {
    createSite(payload, {
      onSuccess: (data) => {
        resolve(new Site(data))
      },
      onError: (error) => {
        reject(error)
      }
    })
  })
}

defineExpose({
  validateForm,
  clearErrors,
  createClientSite
})
</script>
